/**
 * ContactDetailDrawer — Phase 3 Contact Hub.
 *
 * Slide-in panel for a single canonical contact. Two stacked sections:
 *   1. Action bar (tap-to-call, email, log message)
 *   2. Linked Projects (from project_contacts[] reverse lookup)
 *   3. Activity Timeline (reverse chron, filterable, with "Log activity" CTA)
 *
 * Tap-to-call routes through POST /api/contacts/{id}/actions/initiate-call,
 * which logs an in_app_call activity and returns a tel: href. A future
 * telephony connector can swap the href for a server-initiated call without
 * any FE change — the integration seam is on the backend.
 *
 * data-testids: contact-detail-drawer, call-action-btn, email-action-btn,
 * log-activity-btn, activity-timeline-item-{i}, linked-project-{i}.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DockPanel from '@/components/shell/DockPanel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import RelationshipStatusBadge from '@/components/RelationshipStatusBadge';
import {
  Phone, Mail, MessageSquare, Plus, Briefcase, Calendar, FileText,
  PhoneIncoming, PhoneOutgoing, ArrowDownLeft, ArrowUpRight, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const TYPE_ICON = {
  call: Phone,
  sms: MessageSquare,
  email: Mail,
  meeting: Calendar,
  note: FileText,
};

const TYPE_LABEL = {
  call: 'Call', sms: 'Message', email: 'Email', meeting: 'Meeting', note: 'Note',
};

const SOURCE_LABEL = {
  manual: 'Manual',
  in_app_call: 'In-app call',
  gmail: 'Gmail',
  messaging_webhook: 'Project messages',
  telephony: 'Telephony',
};

export default function ContactDetailDrawer({ contact, open, onOpenChange }) {
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showLog, setShowLog] = useState(false);
  const [logForm, setLogForm] = useState({ type: 'note', direction: 'outbound', body: '', subject: '' });
  const [posting, setPosting] = useState(false);
  // Local copy so a status change inside the drawer reflects immediately.
  const [localContact, setLocalContact] = useState(contact);
  useEffect(() => { setLocalContact(contact); }, [contact]);

  const cid = contact?.id;

  const fetchAll = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [actRes, projRes] = await Promise.all([
        axios.get(`${API}/api/contacts/${cid}/activities`, { headers: authHeaders() }),
        axios.get(`${API}/api/contacts/${cid}/projects`, { headers: authHeaders() }),
      ]);
      setActivities(actRes.data?.activities || []);
      setProjects(projRes.data?.projects || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load contact timeline');
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    if (open && cid) fetchAll();
  }, [open, cid, fetchAll]);

  const filteredActivities = typeFilter === 'all'
    ? activities
    : activities.filter(a => a.type === typeFilter);

  const handleCall = async () => {
    if (!contact?.phone && !contact?.phone_mobile && !contact?.phone_work) {
      toast.error('No phone number on file for this contact');
      return;
    }
    try {
      const r = await axios.post(`${API}/api/contacts/${cid}/actions/initiate-call`, {}, { headers: authHeaders() });
      // Open tel: link in a new tab to launch the dialer (or future provider)
      window.location.href = r.data.tel_href;
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Call failed');
    }
  };

  const handleEmail = () => {
    if (!contact?.email) {
      toast.error('No email on file');
      return;
    }
    // For now use mailto: — future connector swaps to in-app Gmail compose.
    window.location.href = `mailto:${contact.email}`;
  };

  const submitLog = async (e) => {
    e?.preventDefault?.();
    setPosting(true);
    try {
      const body = {
        type: logForm.type,
        body: logForm.body,
        subject: logForm.subject || null,
      };
      if (['call', 'sms', 'email'].includes(logForm.type)) {
        body.direction = logForm.direction;
      }
      const r = await axios.post(`${API}/api/contacts/${cid}/activities`, body, { headers: authHeaders() });
      setActivities(prev => [r.data, ...prev]);
      setShowLog(false);
      setLogForm({ type: 'note', direction: 'outbound', body: '', subject: '' });
      toast.success('Activity logged');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log activity');
    } finally {
      setPosting(false);
    }
  };

  if (!contact) return null;

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Contact';
  const phone = contact.phone || contact.phone_mobile || contact.phone_work;

  if (!open) return null;

  // SH-1 — in the shell's dock, beside the page, not over it.
  return (
    <DockPanel onClose={() => onOpenChange(false)} width="640px">
      <div className="h-full overflow-y-auto bg-white p-6" data-testid="contact-detail-drawer">
        <div className="flex flex-col space-y-2 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground">{fullName}</h2>
              <p className="text-sm text-stone-600">
                {contact.email || '—'}
                {phone ? ` · ${phone}` : ''}
                {contact.company ? ` · ${contact.company}` : ''}
              </p>
            </div>
            <RelationshipStatusBadge
              contact={localContact || contact}
              variant="inline"
              onChange={(updated) => {
                setLocalContact(prev => ({ ...(prev || {}), ...updated }));
                // Refresh timeline so the just-written status-change note appears.
                fetchAll();
              }}
            />
          </div>
          {(localContact?.relationship_status === 'warning' || localContact?.relationship_status === 'caution') && localContact?.relationship_status_reason && (
            <p className={`text-xs mt-1.5 italic ${localContact.relationship_status === 'warning' ? 'text-red-700' : 'text-amber-700'}`}>
              &ldquo;{localContact.relationship_status_reason}&rdquo;
            </p>
          )}
          <button type="button" className="self-start text-xs uppercase tracking-wide text-stone-500 hover:text-stone-900"
            onClick={() => onOpenChange(false)} data-testid="contact-detail-close">Close</button>
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-2 mt-4 pb-4 border-b border-stone-200">
          <Button size="sm" onClick={handleCall} disabled={!phone} data-testid="call-action-btn">
            <Phone className="h-3.5 w-3.5 mr-1.5" /> Call
          </Button>
          <Button size="sm" variant="outline" onClick={handleEmail} disabled={!contact.email} data-testid="email-action-btn">
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowLog(v => !v)}
            data-testid="log-activity-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Log activity
          </Button>
        </div>

        {/* Inline log form */}
        {showLog && (
          <form onSubmit={submitLog} className="mt-4 p-3 border border-stone-200 rounded-md bg-stone-50 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={logForm.type}
                onValueChange={(v) => setLogForm(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger data-testid="log-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="sms">Message</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                </SelectContent>
              </Select>
              {['call', 'sms', 'email'].includes(logForm.type) && (
                <Select
                  value={logForm.direction}
                  onValueChange={(v) => setLogForm(prev => ({ ...prev, direction: v }))}
                >
                  <SelectTrigger data-testid="log-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <Textarea
              placeholder="Notes / body"
              value={logForm.body}
              onChange={(e) => setLogForm(prev => ({ ...prev, body: e.target.value }))}
              rows={3}
              data-testid="log-body-input"
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowLog(false)}>Cancel</Button>
              <Button type="submit" size="sm" disabled={posting} data-testid="log-submit-btn">
                {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          </form>
        )}

        <Tabs defaultValue="timeline" className="mt-6">
          <TabsList>
            <TabsTrigger value="timeline" data-testid="tab-timeline">
              Timeline ({activities.length})
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">
              Projects ({projects.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="space-y-3 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-500">Filter:</span>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-7 w-32 text-xs" data-testid="timeline-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="call">Calls</SelectItem>
                  <SelectItem value="sms">Messages</SelectItem>
                  <SelectItem value="email">Emails</SelectItem>
                  <SelectItem value="meeting">Meetings</SelectItem>
                  <SelectItem value="note">Notes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading && (
              <p className="text-sm text-stone-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </p>
            )}
            {!loading && filteredActivities.length === 0 && (
              <p className="text-sm text-stone-500" data-testid="timeline-empty">
                No activities {typeFilter !== 'all' ? `of type ${typeFilter}` : ''} yet.
              </p>
            )}
            {filteredActivities.map((a, i) => {
              const Icon = TYPE_ICON[a.type] || FileText;
              const DirIcon = a.direction === 'inbound' ? ArrowDownLeft
                            : a.direction === 'outbound' ? ArrowUpRight : null;
              const Phn = a.type === 'call' && a.direction === 'inbound' ? PhoneIncoming
                       : a.type === 'call' && a.direction === 'outbound' ? PhoneOutgoing : Icon;
              const ts = new Date(a.timestamp);
              return (
                <div
                  key={a.id}
                  className="flex gap-3 p-3 border border-stone-200 rounded-md bg-white"
                  data-testid={`activity-timeline-item-${i}`}
                >
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-stone-100 flex items-center justify-center">
                    <Phn className="h-4 w-4 text-stone-600" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">{TYPE_LABEL[a.type] || a.type}</span>
                      {DirIcon && <DirIcon className="h-3 w-3 text-stone-400" />}
                      {a.duration_seconds ? (
                        <span className="text-stone-500 text-xs">{Math.round(a.duration_seconds / 60)}m</span>
                      ) : null}
                      <Badge className="text-xs bg-stone-100 text-stone-600 border-stone-200 border ml-auto">
                        {SOURCE_LABEL[a.source] || a.source}
                      </Badge>
                    </div>
                    {a.subject && (
                      <p className="text-sm font-medium text-stone-800">{a.subject}</p>
                    )}
                    {a.body && (
                      <p className="text-sm text-stone-700 whitespace-pre-wrap">{a.body}</p>
                    )}
                    <p className="text-xs text-stone-500">
                      {ts.toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="projects" className="space-y-2 mt-4">
            {loading && (
              <p className="text-sm text-stone-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </p>
            )}
            {!loading && projects.length === 0 && (
              <p className="text-sm text-stone-500" data-testid="projects-empty">
                Not linked to any projects yet.
              </p>
            )}
            {projects.map((p, i) => (
              <a
                key={p.id}
                href={`/projects/${p.id}/hub`}
                className="flex items-start gap-3 p-3 border border-stone-200 rounded-md bg-white hover:bg-stone-50 transition-colors"
                data-testid={`linked-project-${i}`}
              >
                <Briefcase className="h-4 w-4 text-stone-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900">{p.name}</p>
                  <p className="text-xs text-stone-500">
                    {p.project_code}
                    {p.status ? ` · ${p.status}` : ''}
                    {p.site_address ? ` · ${p.site_address}` : ''}
                  </p>
                </div>
              </a>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </DockPanel>
  );
}
